/**
 * /expenses — Lenavio Expense.
 *
 * Three role-aware tabs:
 *
 *   - My reports — every user. Create / edit drafts, attach receipts,
 *     submit. Reports auto-approve when policy thresholds allow.
 *   - Approvals — MANAGER + HR. Queue of submitted reports they can
 *     approve / reject.
 *   - Dashboard — HR_ADMIN+. Pending / approved / reimbursed totals,
 *     plus rollups by department / category / month.
 *
 * Plan-gated by ``expenses`` (Pro+ / trialing).
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import {
  Camera,
  CheckCircle2,
  ChevronRight,
  CircleDollarSign,
  Download,
  ImageIcon,
  Loader2,
  Plus,
  Send,
  Trash2,
  Upload,
  XCircle,
} from "lucide-react";

import { pageTransition } from "@/lib/utils";
import { useToast } from "@/hooks/useToast";
import { useAuth } from "@/hooks/useAuth";

import {
  addItem,
  createReport,
  deleteItem,
  fetchDashboard,
  listMyReports,
  listPending,
  reimburseReport,
  reviewReport,
  submitReport,
  uploadReceipt,
  type ExpenseCategory,
  type ExpenseDashboard,
  type ExpenseItem,
  type ExpenseItemInput,
  type ExpenseReport,
  type ExpenseStatus,
} from "@/api/expenses";

import { PageHeader } from "@/components/layout/PageHeader";
import { ExportCsvButton } from "@/components/shared/ExportCsvButton";
import { ScanReceiptModal } from "@/components/expenses/ScanReceiptModal";
import { Card } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Select } from "@/components/ui/Select";
import { Badge } from "@/components/ui/Badge";
import { Modal } from "@/components/ui/Modal";
import { Tabs, TabPanel } from "@/components/ui/Tabs";

const CATEGORIES: { value: ExpenseCategory; label: string }[] = [
  { value: "travel", label: "Travel" },
  { value: "meals", label: "Meals" },
  { value: "office", label: "Office" },
  { value: "equipment", label: "Equipment" },
  { value: "software", label: "Software" },
  { value: "other", label: "Other" },
];

function fmtMoney(n: number, currency = "USD"): string {
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency,
      maximumFractionDigits: 2,
    }).format(n);
  } catch {
    return `${currency} ${n.toFixed(2)}`;
  }
}

function statusBadge(status: ExpenseStatus) {
  switch (status) {
    case "draft":
      return <Badge variant="default">Draft</Badge>;
    case "submitted":
      return <Badge variant="info">Submitted</Badge>;
    case "approved":
      return <Badge variant="success">Approved</Badge>;
    case "rejected":
      return <Badge variant="danger">Rejected</Badge>;
    case "reimbursed":
      return <Badge variant="success">Reimbursed</Badge>;
  }
}

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

function downloadCsv(filename: string, rows: string[][]) {
  const csv = rows
    .map((r) =>
      r
        .map((cell) => {
          const s = (cell ?? "").toString();
          return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
        })
        .join(","),
    )
    .join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// ════════════════════════════════════════════════════════════════════
// My reports tab
// ════════════════════════════════════════════════════════════════════

function MyReportsTab() {
  const { addToast } = useToast();
  const [reports, setReports] = useState<ExpenseReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [showScan, setShowScan] = useState(false);
  const [openReport, setOpenReport] = useState<ExpenseReport | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const r = await listMyReports();
      setReports(r);
    } catch {
      addToast("error", "Failed to load reports");
    } finally {
      setLoading(false);
    }
  }, [addToast]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-end gap-2">
        <Button variant="secondary" onClick={() => setShowScan(true)}>
          <Camera className="mr-2 h-4 w-4" /> Scan receipt
        </Button>
        <Button onClick={() => setShowCreate(true)}>
          <Plus className="mr-2 h-4 w-4" /> New report
        </Button>
      </div>

      {showScan && (
        <ScanReceiptModal
          onClose={() => {
            setShowScan(false);
            // Refresh so the new line + report show up.
            refresh();
          }}
        />
      )}

      {loading ? (
        <Card className="flex h-32 items-center justify-center text-text-muted">
          <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Loading…
        </Card>
      ) : reports.length === 0 ? (
        <Card className="p-10 text-center text-sm text-text-muted">
          No expense reports yet. Click <strong>New report</strong> to start one.
        </Card>
      ) : (
        <Card>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-surface-muted text-text-muted">
                <tr>
                  <th className="px-4 py-2 text-left">Title</th>
                  <th className="px-4 py-2 text-left">Status</th>
                  <th className="px-4 py-2 text-right">Total</th>
                  <th className="px-4 py-2 text-left">Submitted</th>
                  <th className="px-4 py-2 text-left">Items</th>
                  <th className="px-4 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {reports.map((r) => (
                  <tr
                    key={r.id}
                    className="cursor-pointer border-t border-border hover:bg-surface-muted/40"
                    onClick={() => setOpenReport(r)}
                  >
                    <td className="px-4 py-2 font-medium">{r.title}</td>
                    <td className="px-4 py-2">{statusBadge(r.status)}</td>
                    <td className="px-4 py-2 text-right tabular-nums">
                      {fmtMoney(r.total_amount, r.currency)}
                    </td>
                    <td className="px-4 py-2 text-text-muted">
                      {r.submitted_at
                        ? new Date(r.submitted_at).toLocaleDateString()
                        : "—"}
                    </td>
                    <td className="px-4 py-2 text-text-muted">
                      {r.items.length}
                    </td>
                    <td className="px-4 py-2 text-right">
                      <ChevronRight className="ml-auto h-4 w-4 text-text-muted" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {showCreate && (
        <CreateReportModal
          onClose={() => setShowCreate(false)}
          onCreated={(r) => {
            setShowCreate(false);
            setOpenReport(r);
            refresh();
          }}
        />
      )}
      {openReport && (
        <ReportDetailModal
          report={openReport}
          onClose={() => setOpenReport(null)}
          onChanged={async () => {
            await refresh();
          }}
        />
      )}
    </div>
  );
}

function CreateReportModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (r: ExpenseReport) => void;
}) {
  const { addToast } = useToast();
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [currency, setCurrency] = useState("USD");
  const [busy, setBusy] = useState(false);

  async function onSubmit() {
    if (!title.trim()) {
      addToast("error", "Title is required");
      return;
    }
    setBusy(true);
    try {
      const r = await createReport({
        title: title.trim(),
        description: description.trim() || null,
        currency: currency.trim().toUpperCase() || "USD",
        items: [],
      });
      addToast("success", "Draft report created");
      onCreated(r);
    } catch {
      addToast("error", "Failed to create report");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal isOpen onClose={onClose} title="New expense report">
      <div className="space-y-4">
        <Input
          label="Title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="e.g. London Conference — Mar 2026"
        />
        <Input
          label="Description (optional)"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Trip, project, or other context"
        />
        <Input
          label="Currency"
          value={currency}
          onChange={(e) => setCurrency(e.target.value)}
        />
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={onSubmit} disabled={busy}>
            {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Create
          </Button>
        </div>
      </div>
    </Modal>
  );
}

function ReportDetailModal({
  report: initial,
  onClose,
  onChanged,
}: {
  report: ExpenseReport;
  onClose: () => void;
  onChanged: () => Promise<void> | void;
}) {
  const { addToast } = useToast();
  const [report, setReport] = useState<ExpenseReport>(initial);
  const [busy, setBusy] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [showScan, setShowScan] = useState(false);

  const isDraft = report.status === "draft";

  async function onSubmitReport() {
    setBusy(true);
    try {
      const r = await submitReport(report.id);
      setReport(r);
      addToast(
        "success",
        r.status === "approved"
          ? "Auto-approved under policy"
          : "Submitted for approval",
      );
      await onChanged();
    } catch (err: unknown) {
      addToast(
        "error",
        (err as { response?: { data?: { detail?: string } } })?.response?.data
          ?.detail || "Submit failed",
      );
    } finally {
      setBusy(false);
    }
  }

  async function onDeleteItem(item: ExpenseItem) {
    if (!confirm(`Delete "${item.description}"?`)) return;
    setBusy(true);
    try {
      await deleteItem(item.id);
      setReport({
        ...report,
        items: report.items.filter((i) => i.id !== item.id),
        total_amount: report.total_amount - item.amount,
      });
      await onChanged();
    } catch {
      addToast("error", "Failed to remove item");
    } finally {
      setBusy(false);
    }
  }

  async function onUploadReceipt(item: ExpenseItem, file: File) {
    setBusy(true);
    try {
      const updated = await uploadReceipt(item.id, file);
      setReport({
        ...report,
        items: report.items.map((i) => (i.id === item.id ? updated : i)),
      });
      addToast("success", "Receipt attached");
    } catch {
      addToast("error", "Receipt upload failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal isOpen onClose={onClose} title={report.title} size="lg">
      <div className="space-y-5">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            {statusBadge(report.status)}
            <span className="text-sm text-text-muted">
              Total{" "}
              <span className="font-semibold text-text-primary tabular-nums">
                {fmtMoney(report.total_amount, report.currency)}
              </span>
            </span>
          </div>
          {isDraft && (
            <div className="flex gap-2">
              <Button variant="ghost" onClick={() => setShowScan(true)}>
                <Camera className="mr-1 h-4 w-4" /> Scan receipt
              </Button>
              <Button variant="ghost" onClick={() => setShowAdd(true)}>
                <Plus className="mr-1 h-4 w-4" /> Add item
              </Button>
              <Button onClick={onSubmitReport} disabled={busy || report.items.length === 0}>
                <Send className="mr-2 h-4 w-4" /> Submit
              </Button>
            </div>
          )}
        </div>
        {report.description && (
          <p className="text-sm text-text-muted">{report.description}</p>
        )}

        <div className="overflow-x-auto rounded-md border border-border">
          <table className="min-w-full text-sm">
            <thead className="bg-surface-muted text-text-muted">
              <tr>
                <th className="px-3 py-2 text-left">Date</th>
                <th className="px-3 py-2 text-left">Category</th>
                <th className="px-3 py-2 text-left">Description</th>
                <th className="px-3 py-2 text-right">Amount</th>
                <th className="px-3 py-2 text-left">Receipt</th>
                {isDraft && <th />}
              </tr>
            </thead>
            <tbody>
              {report.items.length === 0 ? (
                <tr>
                  <td
                    colSpan={isDraft ? 6 : 5}
                    className="py-6 text-center text-text-muted"
                  >
                    No items yet.
                  </td>
                </tr>
              ) : (
                report.items.map((it) => (
                  <tr key={it.id} className="border-t border-border">
                    <td className="px-3 py-2 text-text-muted">
                      {it.item_date}
                    </td>
                    <td className="px-3 py-2 capitalize">{it.category}</td>
                    <td className="px-3 py-2">
                      {it.description}
                      {it.merchant_name && (
                        <span className="text-text-muted">
                          {" "}
                          · {it.merchant_name}
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">
                      {fmtMoney(it.amount, it.currency)}
                    </td>
                    <td className="px-3 py-2">
                      <ReceiptCell
                        item={it}
                        canUpload={isDraft || report.status === "submitted"}
                        onUpload={(f) => onUploadReceipt(it, f)}
                      />
                    </td>
                    {isDraft && (
                      <td className="px-3 py-2 text-right">
                        <button
                          onClick={() => onDeleteItem(it)}
                          className="text-text-muted hover:text-danger"
                          aria-label="Remove item"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </td>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {report.review_notes && (
          <div className="rounded-md border border-border bg-surface-muted p-3 text-sm">
            <div className="mb-1 font-medium">Reviewer notes</div>
            <div className="text-text-muted">{report.review_notes}</div>
          </div>
        )}
      </div>

      {showAdd && (
        <AddItemModal
          reportId={report.id}
          currency={report.currency}
          onClose={() => setShowAdd(false)}
          onAdded={(item) => {
            setReport({
              ...report,
              items: [...report.items, item],
              total_amount: report.total_amount + item.amount,
            });
            setShowAdd(false);
            void onChanged();
          }}
        />
      )}
      {showScan && (
        <ScanReceiptModal
          reportId={report.id}
          onClose={() => {
            setShowScan(false);
            // Re-fetch the parent so the new line + receipt URL show
            // up. We don't merge into the local ``report`` state
            // because the OCR-created item lives on a (possibly
            // different) draft if the current one is no longer draft.
            void onChanged();
          }}
        />
      )}
    </Modal>
  );
}

function ReceiptCell({
  item,
  canUpload,
  onUpload,
}: {
  item: ExpenseItem;
  canUpload: boolean;
  onUpload: (f: File) => void;
}) {
  if (item.receipt_url) {
    return (
      <a
        href={item.receipt_url}
        target="_blank"
        rel="noreferrer"
        className="inline-flex items-center gap-1 text-accent hover:underline"
      >
        <ImageIcon className="h-4 w-4" /> View
      </a>
    );
  }
  if (!canUpload) return <span className="text-text-muted">—</span>;
  return (
    <label className="inline-flex cursor-pointer items-center gap-1 text-text-muted hover:text-text-primary">
      <Upload className="h-4 w-4" /> Upload
      <input
        type="file"
        className="hidden"
        accept="image/*,application/pdf"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) onUpload(f);
          e.target.value = "";
        }}
      />
    </label>
  );
}

function AddItemModal({
  reportId,
  currency,
  onClose,
  onAdded,
}: {
  reportId: string;
  currency: string;
  onClose: () => void;
  onAdded: (i: ExpenseItem) => void;
}) {
  const { addToast } = useToast();
  const [form, setForm] = useState<ExpenseItemInput>({
    description: "",
    category: "other",
    amount: 0,
    currency,
    item_date: todayIso(),
    merchant_name: "",
    notes: "",
  });
  const [busy, setBusy] = useState(false);

  async function onSubmit() {
    if (!form.description.trim() || !form.amount) {
      addToast("error", "Description and amount are required");
      return;
    }
    setBusy(true);
    try {
      const item = await addItem(reportId, {
        ...form,
        description: form.description.trim(),
        merchant_name: form.merchant_name?.trim() || null,
        notes: form.notes?.trim() || null,
      });
      onAdded(item);
    } catch (err: unknown) {
      addToast(
        "error",
        (err as { response?: { data?: { detail?: string } } })?.response?.data
          ?.detail || "Failed to add item",
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal isOpen onClose={onClose} title="Add line item">
      <div className="space-y-3">
        <Input
          label="Description"
          value={form.description}
          onChange={(e) => setForm({ ...form, description: e.target.value })}
        />
        <div className="grid grid-cols-2 gap-3">
          <Select
            label="Category"
            value={form.category}
            onChange={(e) =>
              setForm({ ...form, category: e.target.value as ExpenseCategory })
            }
            options={CATEGORIES.map((c) => ({ value: c.value, label: c.label }))}
          />
          <Input
            label="Date"
            type="date"
            value={form.item_date}
            onChange={(e) => setForm({ ...form, item_date: e.target.value })}
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Input
            label="Amount"
            type="number"
            step="0.01"
            value={String(form.amount)}
            onChange={(e) =>
              setForm({ ...form, amount: parseFloat(e.target.value) || 0 })
            }
          />
          <Input
            label="Currency"
            value={form.currency || currency}
            onChange={(e) => setForm({ ...form, currency: e.target.value })}
          />
        </div>
        <Input
          label="Merchant (optional)"
          value={form.merchant_name || ""}
          onChange={(e) =>
            setForm({ ...form, merchant_name: e.target.value })
          }
        />
        <Input
          label="Notes (optional)"
          value={form.notes || ""}
          onChange={(e) => setForm({ ...form, notes: e.target.value })}
        />
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={onSubmit} disabled={busy}>
            {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Add
          </Button>
        </div>
      </div>
    </Modal>
  );
}

// ════════════════════════════════════════════════════════════════════
// Approvals tab
// ════════════════════════════════════════════════════════════════════

function ApprovalsTab() {
  const { addToast } = useToast();
  const { user } = useAuth();
  const isHr = user?.role === "HR_ADMIN" || user?.role === "SUPER_ADMIN";
  const [pending, setPending] = useState<ExpenseReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const r = await listPending();
      setPending(r);
    } catch {
      addToast("error", "Failed to load approvals");
    } finally {
      setLoading(false);
    }
  }, [addToast]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  async function onDecide(report: ExpenseReport, approve: boolean) {
    const notes = approve ? undefined : prompt("Reason for rejection?") || "";
    setBusy(report.id);
    try {
      await reviewReport(report.id, approve, notes);
      addToast("success", approve ? "Approved" : "Rejected");
      await refresh();
    } catch (err: unknown) {
      addToast(
        "error",
        (err as { response?: { data?: { detail?: string } } })?.response?.data
          ?.detail || "Action failed",
      );
    } finally {
      setBusy(null);
    }
  }

  async function onReimburse(report: ExpenseReport) {
    setBusy(report.id);
    try {
      await reimburseReport(report.id);
      addToast("success", "Marked reimbursed");
      await refresh();
    } catch (err: unknown) {
      addToast(
        "error",
        (err as { response?: { data?: { detail?: string } } })?.response?.data
          ?.detail || "Action failed",
      );
    } finally {
      setBusy(null);
    }
  }

  if (loading) {
    return (
      <Card className="flex h-32 items-center justify-center text-text-muted">
        <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Loading…
      </Card>
    );
  }
  if (pending.length === 0) {
    return (
      <Card className="p-10 text-center text-sm text-text-muted">
        Nothing pending. Submitted reports from your team will land here.
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {pending.map((r) => (
        <Card key={r.id} className="p-5">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <div className="flex items-center gap-2">
                <h3 className="text-base font-semibold">{r.title}</h3>
                {statusBadge(r.status)}
              </div>
              <div className="mt-1 text-sm text-text-muted">
                {r.employee_name || "—"}
                {r.department_name && (
                  <span> · {r.department_name}</span>
                )}{" "}
                · Submitted{" "}
                {r.submitted_at
                  ? new Date(r.submitted_at).toLocaleDateString()
                  : "—"}
              </div>
            </div>
            <div className="text-right">
              <div className="text-2xl font-semibold tabular-nums">
                {fmtMoney(r.total_amount, r.currency)}
              </div>
              <div className="text-xs text-text-muted">
                {r.items.length} item{r.items.length === 1 ? "" : "s"}
              </div>
            </div>
          </div>

          <ul className="mt-3 divide-y divide-border rounded-md border border-border">
            {r.items.slice(0, 4).map((it) => (
              <li
                key={it.id}
                className="flex items-center justify-between gap-3 px-3 py-2 text-sm"
              >
                <div className="min-w-0 flex-1 truncate">
                  <span className="capitalize text-text-muted">
                    {it.category}
                  </span>{" "}
                  · {it.description}
                </div>
                <div className="flex items-center gap-3">
                  {it.receipt_url && (
                    <a
                      href={it.receipt_url}
                      target="_blank"
                      rel="noreferrer"
                      className="text-accent hover:underline"
                    >
                      Receipt
                    </a>
                  )}
                  <span className="tabular-nums">
                    {fmtMoney(it.amount, it.currency)}
                  </span>
                </div>
              </li>
            ))}
            {r.items.length > 4 && (
              <li className="px-3 py-2 text-xs text-text-muted">
                +{r.items.length - 4} more…
              </li>
            )}
          </ul>

          <div className="mt-4 flex justify-end gap-2">
            <Button
              variant="danger"
              onClick={() => onDecide(r, false)}
              disabled={busy === r.id}
            >
              <XCircle className="mr-2 h-4 w-4" /> Reject
            </Button>
            <Button onClick={() => onDecide(r, true)} disabled={busy === r.id}>
              <CheckCircle2 className="mr-2 h-4 w-4" /> Approve
            </Button>
            {isHr && r.status === "approved" && (
              <Button
                variant="secondary"
                onClick={() => onReimburse(r)}
                disabled={busy === r.id}
              >
                <CircleDollarSign className="mr-2 h-4 w-4" /> Mark reimbursed
              </Button>
            )}
          </div>
        </Card>
      ))}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// HR dashboard tab
// ════════════════════════════════════════════════════════════════════

function DashboardTab() {
  const { addToast } = useToast();
  const [data, setData] = useState<ExpenseDashboard | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetchDashboard();
        if (!cancelled) setData(r);
      } catch {
        if (!cancelled) addToast("error", "Failed to load dashboard");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [addToast]);

  const monthMax = useMemo(() => {
    if (!data) return 1;
    return Math.max(1, ...data.by_month.map((m) => m.total_amount)) * 1.1;
  }, [data]);

  const catMax = useMemo(() => {
    if (!data) return 1;
    return Math.max(1, ...data.by_category.map((c) => c.total_amount)) * 1.1;
  }, [data]);

  const deptMax = useMemo(() => {
    if (!data) return 1;
    return Math.max(1, ...data.by_department.map((d) => d.total_amount)) * 1.1;
  }, [data]);

  function onExport() {
    if (!data) return;
    const rows: string[][] = [
      ["Section", "Key", "Count", "Amount"],
      [
        "Summary",
        "Pending",
        String(data.pending_count),
        String(data.pending_amount),
      ],
      [
        "Summary",
        "Approved",
        String(data.approved_count),
        String(data.approved_amount),
      ],
      [
        "Summary",
        "Reimbursed",
        String(data.reimbursed_count),
        String(data.reimbursed_amount),
      ],
    ];
    for (const c of data.by_category)
      rows.push(["Category", c.category, String(c.count), String(c.total_amount)]);
    for (const d of data.by_department)
      rows.push([
        "Department",
        d.department_name || "(no department)",
        String(d.employee_count),
        String(d.total_amount),
      ]);
    for (const m of data.by_month)
      rows.push(["Month", m.month, String(m.report_count), String(m.total_amount)]);
    downloadCsv("expenses-dashboard.csv", rows);
  }

  if (loading) {
    return (
      <Card className="flex h-32 items-center justify-center text-text-muted">
        <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Loading…
      </Card>
    );
  }
  if (!data) return null;

  return (
    <div className="space-y-6">
      <Card className="p-5">
        <div className="flex items-center justify-between gap-3">
          <div className="grid grid-cols-2 gap-6 md:grid-cols-3">
            <Stat
              label="Pending"
              value={fmtMoney(data.pending_amount, data.currency)}
              sub={`${data.pending_count} report${data.pending_count === 1 ? "" : "s"}`}
            />
            <Stat
              label="Approved"
              value={fmtMoney(data.approved_amount, data.currency)}
              sub={`${data.approved_count} report${data.approved_count === 1 ? "" : "s"}`}
            />
            <Stat
              label="Reimbursed"
              value={fmtMoney(data.reimbursed_amount, data.currency)}
              sub={`${data.reimbursed_count} report${data.reimbursed_count === 1 ? "" : "s"}`}
            />
          </div>
          <Button variant="ghost" onClick={onExport}>
            <Download className="mr-2 h-4 w-4" /> Export CSV
          </Button>
        </div>
      </Card>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Card className="p-5">
          <div className="mb-3 text-sm font-medium">By category</div>
          {data.by_category.length === 0 ? (
            <p className="text-sm text-text-muted">No spend yet.</p>
          ) : (
            <div className="space-y-2">
              {data.by_category.map((c) => {
                const w = (c.total_amount / catMax) * 100;
                return (
                  <div key={c.category}>
                    <div className="flex items-center justify-between text-sm">
                      <span className="capitalize">{c.category}</span>
                      <span className="tabular-nums">
                        {fmtMoney(c.total_amount, data.currency)}
                      </span>
                    </div>
                    <div className="mt-1 h-2 w-full rounded-full bg-surface-muted">
                      <div
                        className="h-2 rounded-full bg-accent"
                        style={{ width: `${Math.max(2, w)}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </Card>

        <Card className="p-5">
          <div className="mb-3 text-sm font-medium">By department</div>
          {data.by_department.length === 0 ? (
            <p className="text-sm text-text-muted">No data.</p>
          ) : (
            <div className="space-y-2">
              {data.by_department.map((d, i) => {
                const w = (d.total_amount / deptMax) * 100;
                return (
                  <div key={d.department_id || `none-${i}`}>
                    <div className="flex items-center justify-between text-sm">
                      <span>
                        {d.department_name || "(no department)"}{" "}
                        <span className="text-text-muted">
                          ({d.employee_count})
                        </span>
                      </span>
                      <span className="tabular-nums">
                        {fmtMoney(d.total_amount, data.currency)}
                      </span>
                    </div>
                    <div className="mt-1 h-2 w-full rounded-full bg-surface-muted">
                      <div
                        className="h-2 rounded-full bg-accent"
                        style={{ width: `${Math.max(2, w)}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </Card>
      </div>

      <Card className="p-5">
        <div className="mb-3 text-sm font-medium">Monthly trend</div>
        {data.by_month.length === 0 ? (
          <p className="text-sm text-text-muted">No data.</p>
        ) : (
          <div className="flex h-40 items-end gap-2">
            {data.by_month.map((m) => {
              const h = (m.total_amount / monthMax) * 100;
              return (
                <div
                  key={m.month}
                  className="flex flex-1 flex-col items-center gap-1"
                  title={`${m.month} · ${fmtMoney(m.total_amount, data.currency)} · ${m.report_count} reports`}
                >
                  <div
                    className="w-full rounded-t bg-accent"
                    style={{ height: `${Math.max(2, h)}%` }}
                  />
                  <div className="text-xs text-text-muted">{m.month}</div>
                </div>
              );
            })}
          </div>
        )}
      </Card>
    </div>
  );
}

function Stat({
  label,
  value,
  sub,
}: {
  label: string;
  value: string;
  sub?: string;
}) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wide text-text-muted">
        {label}
      </div>
      <div className="mt-1 text-2xl font-semibold tabular-nums">{value}</div>
      {sub && <div className="text-xs text-text-muted">{sub}</div>}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// Page shell
// ════════════════════════════════════════════════════════════════════

type TabId = "mine" | "approvals" | "dashboard";

export function ExpensesPage() {
  const { user } = useAuth();
  const role = user?.role;
  const showApprovals = role === "MANAGER" || role === "HR_ADMIN" || role === "SUPER_ADMIN";
  const showDashboard = role === "HR_ADMIN" || role === "SUPER_ADMIN";

  const [tab, setTab] = useState<TabId>("mine");

  const tabs: { id: TabId; label: string }[] = [
    { id: "mine", label: "My reports" },
  ];
  if (showApprovals) tabs.push({ id: "approvals", label: "Approvals" });
  if (showDashboard) tabs.push({ id: "dashboard", label: "Dashboard" });

  return (
    <motion.div {...pageTransition} className="space-y-6">
      <PageHeader
        title="Expenses"
        subtitle="Submit expense reports, approve direct-report submissions, and watch org spend."
        actions={
          showDashboard ? <ExportCsvButton resource="expenses" /> : undefined
        }
      />
      <Tabs
        tabs={tabs}
        activeTab={tab}
        onChange={(id) => setTab(id as TabId)}
        aria-label="Expenses sections"
      />
      <TabPanel id="mine" activeTab={tab}>
        <MyReportsTab />
      </TabPanel>
      {showApprovals && (
        <TabPanel id="approvals" activeTab={tab}>
          <ApprovalsTab />
        </TabPanel>
      )}
      {showDashboard && (
        <TabPanel id="dashboard" activeTab={tab}>
          <DashboardTab />
        </TabPanel>
      )}
    </motion.div>
  );
}

export default ExpensesPage;
